import type { Dispatch, SetStateAction } from "react";
import { Fragment } from "react";

import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import {
  MAX_AUDIO_LENGTH_SECONDS,
  MAX_IMAGE_SIZE_BYTES,
  MAX_VIDEO_SIZE_BYTES,
} from "@/utils/constants";
import type {
  AudioMetadata,
  MediaItem,
  VideoMetadata,
} from "@/utils/media-utils";
import {
  extractMetadataFromAudio,
  extractMetadataFromImage,
  extractMetadataFromVideo,
  handleAudioUpload,
  handleImageUpload,
  handleVideoUpload,
} from "@/utils/media-utils";

import { EditorMediaType } from "./constants";

export type EditorUploadLimit = {
  maxFileSize?: number;
  maxLength?: number;
};

export const EDITOR_MEDIA_LIMITS: Record<EditorMediaType, EditorUploadLimit> = {
  [EditorMediaType.Image]: {
    maxFileSize: MAX_IMAGE_SIZE_BYTES,
  },
  [EditorMediaType.Video]: {
    maxFileSize: MAX_VIDEO_SIZE_BYTES,
  },
  [EditorMediaType.Audio]: {
    maxFileSize: MAX_VIDEO_SIZE_BYTES,
    maxLength: MAX_AUDIO_LENGTH_SECONDS,
  },
};

const bytesToMegabytes = (bytes: number) => bytes / 1024 / 1024;

const IMAGE_EXTENSIONS = new Set([
  "avif",
  "bmp",
  "gif",
  "heic",
  "heif",
  "jpeg",
  "jpg",
  "png",
  "svg",
  "webp",
]);

const VIDEO_EXTENSIONS = new Set([
  "avi",
  "m4v",
  "mkv",
  "mov",
  "mp4",
  "ogv",
  "webm",
]);

const AUDIO_EXTENSIONS = new Set([
  "aac",
  "flac",
  "m4a",
  "mp3",
  "oga",
  "ogg",
  "opus",
  "wav",
]);

const parseMediaItems = async (
  files: File[],
  type: EditorMediaType,
  limit: { maxFileSize?: number; maxLength?: number },
): Promise<MediaItem[]> => {
  return Promise.all(
    files.map(async (file) => {
      let src = "";
      let metadata = null;

      if (type === "video") {
        metadata = await extractMetadataFromVideo(file);
        src = metadata.thumbnailDataURL;
      } else if (type === "image") {
        metadata = await extractMetadataFromImage(file);
        src = metadata.thumbnailDataURL;
      } else if (type === "audio") {
        metadata = await extractMetadataFromAudio(file);
      } else {
        throw new Error("Invalid media type");
      }

      const mediaItem: MediaItem = {
        file,
        type,
        src,
        id: crypto.randomUUID(),
        metadata,
        limitErrors: [],
      };

      if (type === "video") {
        mediaItem.duration = (metadata as VideoMetadata).duration;
        mediaItem.progress = null;
      }

      if (type === "audio") {
        mediaItem.duration = (metadata as AudioMetadata).duration;
        mediaItem.progress = null;
      }

      if (limit.maxFileSize && file.size > limit.maxFileSize) {
        mediaItem.limitErrors.push(
          `The file size of ${file.name} is too large (${bytesToMegabytes(file.size)}). The limit is ${bytesToMegabytes(limit.maxFileSize)}.`,
        );
      }

      if (
        limit.maxLength &&
        mediaItem.duration &&
        mediaItem.duration > limit.maxLength
      ) {
        mediaItem.limitErrors.push(
          `The duration of ${file.name} is too long (${mediaItem.duration} seconds). The limit is ${limit.maxLength} seconds.`,
        );
      }

      return mediaItem;
    }),
  );
};

const removeAlreadyExistingFiles = (value: MediaItem[], files: File[]) => {
  const fileNames = new Set(value.map((item) => item.file.name));

  return files.filter((file) => {
    if (fileNames.has(file.name)) {
      return false;
    }

    fileNames.add(file.name);
    return true;
  });
};

const getEditorMediaTypeFromFile = (file: File) => {
  if (file.type.startsWith("image/")) {
    return EditorMediaType.Image;
  }

  if (file.type.startsWith("video/")) {
    return EditorMediaType.Video;
  }

  if (file.type.startsWith("audio/")) {
    return EditorMediaType.Audio;
  }

  const extension = file.name.split(".").pop()?.toLowerCase();

  if (!extension) {
    return null;
  }

  if (IMAGE_EXTENSIONS.has(extension)) {
    return EditorMediaType.Image;
  }

  if (VIDEO_EXTENSIONS.has(extension)) {
    return EditorMediaType.Video;
  }

  if (AUDIO_EXTENSIONS.has(extension)) {
    return EditorMediaType.Audio;
  }

  return null;
};

const showLimitErrors = (limitFailedItems: MediaItem[]) => {
  if (!limitFailedItems.length) {
    return;
  }

  toast({
    variant: "destructive",
    title: "Limit Error - Files have been removed",
    description: (
      <ul>
        {limitFailedItems.map((item, index) => (
          <Fragment key={index}>
            {item.limitErrors.map((error, errorIndex) => (
              <li key={errorIndex}>{error}</li>
            ))}
          </Fragment>
        ))}
      </ul>
    ),
  });
};

const showUnsupportedFiles = (files: File[]) => {
  if (!files.length) {
    return;
  }

  const fileNames = files.map((file) => file.name);
  const visibleNames = fileNames.slice(0, 3).join(", ");
  const hiddenFilesCount = fileNames.length - 3;
  const hiddenFilesLabel =
    hiddenFilesCount > 0 ? ` and ${hiddenFilesCount} more` : "";

  toast({
    variant: "destructive",
    title: "Unsupported file type",
    description: `Only images, videos, and audio files can be attached. Ignored: ${visibleNames}${hiddenFilesLabel}.`,
  });
};

const prepareMediaItems = async ({
  files,
  type,
  limit = {},
  value,
}: {
  files: File[];
  type: EditorMediaType;
  limit?: EditorUploadLimit;
  value: MediaItem[];
}) => {
  const uniqueFiles = removeAlreadyExistingFiles(value, files);

  if (!uniqueFiles.length) {
    return [];
  }

  try {
    const limitFailedItems: MediaItem[] = [];
    const mediaItems = await parseMediaItems(uniqueFiles, type, limit);
    const validMediaItems = mediaItems.filter((item) => {
      if (item.limitErrors.length) {
        limitFailedItems.push(item);
        return false;
      }

      return true;
    });

    showLimitErrors(limitFailedItems);

    return validMediaItems;
  } catch (error) {
    toast({
      variant: "destructive",
      title: "Error",
      description: `Failed to read files`,
      details: createToastErrorDetails(error),
    });

    return [];
  }
};

const uploadMediaItems = ({
  type,
  value,
  setEditorValue,
  currentUserPublicKey,
}: {
  type: EditorMediaType;
  value: MediaItem[];
  setEditorValue: Dispatch<SetStateAction<MediaItem[]>>;
  currentUserPublicKey: string;
}) => {
  try {
    if (type === EditorMediaType.Image) {
      setEditorValue(value);
      handleImageUpload(value, setEditorValue, currentUserPublicKey);
      return;
    }

    if (type === EditorMediaType.Video) {
      void handleVideoUpload(value, setEditorValue);
      return;
    }

    if (type === EditorMediaType.Audio) {
      void handleAudioUpload(value, setEditorValue);
      return;
    }

    throw new Error("Unsupported media type");
  } catch (error) {
    toast({
      variant: "destructive",
      title: "Error",
      description: `Failed to upload files`,
      details: createToastErrorDetails(error),
    });
  }
};

export const addFilesToEditorMediaField = async ({
  files,
  type,
  limit = {},
  value,
  setEditorValue,
  currentUserPublicKey,
}: {
  files: File[];
  type: EditorMediaType;
  limit?: EditorUploadLimit;
  value: MediaItem[];
  setEditorValue: Dispatch<SetStateAction<MediaItem[]>>;
  currentUserPublicKey: string;
}) => {
  const mediaItems = await prepareMediaItems({
    files,
    type,
    limit,
    value,
  });

  if (!mediaItems.length) {
    return;
  }

  uploadMediaItems({
    type,
    value: [...value, ...mediaItems],
    setEditorValue,
    currentUserPublicKey,
  });
};

export const addDroppedFilesToEditorMediaField = async ({
  files,
  value,
  setEditorValue,
  currentUserPublicKey,
}: {
  files: File[];
  value: MediaItem[];
  setEditorValue: Dispatch<SetStateAction<MediaItem[]>>;
  currentUserPublicKey: string;
}) => {
  const filesByType: Record<EditorMediaType, File[]> = {
    [EditorMediaType.Image]: [],
    [EditorMediaType.Video]: [],
    [EditorMediaType.Audio]: [],
  };
  const unsupportedFiles: File[] = [];

  for (const file of files) {
    const type = getEditorMediaTypeFromFile(file);

    if (!type) {
      unsupportedFiles.push(file);
      continue;
    }

    filesByType[type].push(file);
  }

  showUnsupportedFiles(unsupportedFiles);

  let nextValue = value;

  for (const type of [
    EditorMediaType.Image,
    EditorMediaType.Video,
    EditorMediaType.Audio,
  ]) {
    const mediaItems = await prepareMediaItems({
      files: filesByType[type],
      type,
      limit: EDITOR_MEDIA_LIMITS[type],
      value: nextValue,
    });

    if (!mediaItems.length) {
      continue;
    }

    nextValue = [...nextValue, ...mediaItems];

    uploadMediaItems({
      type,
      value: nextValue,
      setEditorValue,
      currentUserPublicKey,
    });
  }
};
